import { useEffect, useRef } from "react";

/**
 * Runs `fn` every `intervalMs` ONLY while the tab is visible.
 * Pauses when the tab is hidden and runs `fn` once on visibility restore.
 * Use for any periodic Supabase polling to avoid burning Cloud usage on
 * background tabs.
 */
export function useVisibilityPolling(
  fn: () => void | Promise<void>,
  intervalMs: number,
  enabled: boolean = true
) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return;

    let timer: number | null = null;

    const start = () => {
      if (timer != null) return;
      timer = window.setInterval(() => {
        if (document.visibilityState === "visible") {
          fnRef.current();
        }
      }, intervalMs);
    };

    const stop = () => {
      if (timer != null) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Catch up immediately on re-focus, then resume polling.
        fnRef.current();
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs, enabled]);
}
